import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { alpha, Box, Chip, Grid, InputAdornment, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useDealData } from 'api/aws/deals/useDealData'
import { useDealStatusUpdateHook } from 'api/aws/organizations_table/useDealStatusUpdateHook'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUpdateOrgTableItem } from 'api/aws/organizations_table/useUpdateOrgTableItem'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import SingleUserSelect from 'components/Forms/SingleUserSelect'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { buildMasterAgreements } from 'utils/agreementVersioning'
import { useCreateTimeline } from '../../api/aws/useCreateTimeline'
import { useActiveUser } from '../../api/slack/useActiveUser'
import { useSnackbarContext } from '../../context/SnackbarProvider/SnackbarProvider'
import SelectAgreementOption from '../CreateDeal/SelectAgreementOption'
import SelectInvoiceOption from '../CreateDeal/SelectInvoiceOption'
import { dealStageOptions } from '../DataGrid/filterArrays'
import { renderColorByString } from '../DataGrid/renderColorByString'
import { formatNumber } from '../DealsKanban/aggregateStageTotals'
import CustomOptionSelect from '../shared/Select/CustomOptionSelect'
import { updateDealTimelineData } from '../Timeline/createTimelineData'
import LabelValue from './cards/LabelValue'
import DealStatusModal from './DealStatusModal'
import DealStatusSelect from './DealStatusSelect'
import RenderDealInfoLink from './RenderDealInfoLink'
import { InfoLabel, StyledLink } from './style'
import { handleCopy } from './utils/handleCopy'

// ─── DealChainNode ───────────────────────────────────────────────────

const DealChainNode = ({
  deal,
  label,
  version,
  isCurrent = false,
  onClick,
}: {
  deal: any
  label: string
  version?: string
  isCurrent?: boolean
  onClick?: () => void
}) => {
  const statusColor = renderColorByString(deal?.dealStatus)
  const labelColor = label === 'Current' ? 'text.primary' : renderColorByString(label)

  return (
    <Box
      onClick={!isCurrent ? onClick : undefined}
      sx={{
        border: '1px solid',
        borderColor: isCurrent ? 'primary.main' : 'divider',
        borderWidth: isCurrent ? 2 : 1,
        borderRadius: 1,
        px: 1,
        py: 0.75,
        flex: 1,
        minWidth: 100,
        ...(!isCurrent && onClick && { cursor: 'pointer', '&:hover': { opacity: 0.7 } }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: labelColor }}>{label}</Typography>
        {version && (
          <Chip
            label={`v${version}`}
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              fontFamily: 'monospace',
              bgcolor: isCurrent ? 'primary.main' : 'action.selected',
              color: isCurrent ? 'primary.contrastText' : 'text.primary',
            }}
          />
        )}
      </Box>
      <Typography noWrap title={deal?.name} sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.25 }}>
        {deal?.name}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColor, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 11, color: statusColor, fontWeight: 500 }}>
          {deal?.dealStatus || deal?.stage}
        </Typography>
      </Box>
    </Box>
  )
}

const DealInfoCard = ({ newContacts }) => {
  const { PK, SK } = useParams()
  const dealId = SK?.split('DEAL_')?.[1]
  const { deal, productRows, computeTotals } = useDealData(PK, SK, dealId)
  const displayTotals = useMemo(() => {
    // Compute live totals from products instead of relying on stale deal.totals
    const base = deal && productRows?.length ? computeTotals(deal, productRows) : deal?.totals ?? null
    if (!base) return null

    // normalize CSA check + n
    const type = String(deal?.type ?? '').toUpperCase()
    const isCSA = type === 'CSA' || type === 'RCSA'
    const toNum = v => (typeof v === 'string' ? parseFloat(v) || 0 : Number(v || 0))

    const n = isCSA ? Math.max(1, toNum(deal?.numberOfPayments)) : 1
    // always (re)derive paymentAmount from the displayed total
    const paymentAmount =
      isCSA && n > 0 ? Math.round(((base.monthlyTotal || 0) / n + Number.EPSILON) * 100) / 100 : undefined
    return paymentAmount != null ? { ...base, paymentAmount } : base
  }, [deal, productRows, computeTotals])
  const { syncDealFields } = useSyncQueryCache()
  const [validationErrors, setValidationErrors] = useState({
    organization: false,
    type: false,
    startDate: false,
    endDate: false,
  })
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbarContext()
  const { activeUser } = useActiveUser()
  const { mutate: updateItem } = useUpdateOrgTableItem()
  const { handleDealStatusModalUpdate } = useDealStatusUpdateHook(deal, productRows)
  const { mutate: createTimelineEntry } = useCreateTimeline()
  const orgId = deal && deal.organizations && Array.isArray(deal.organizations) ? deal.organizations[0] : ''
  const theme = useTheme()
  const { data: users, isLoading: isUsersLoading } = useUserItems()
  const { deals, organizations, products: allProducts } = useEntitiesFromSearch()
  const organization = organizations?.find(o => o?.PK === PK)
  const contractedBy = organizations?.find(o => o?.PK === `ORG_${organization?.contractedBy?.[0]}`)
  const [showModal, setShowModal] = useState(false) // To control the modal visibility
  const [isModalSave, setIsModalSave] = useState(false)
  const [tempDealStatus, setTempDealStatus] = useState('')
  const [invoiceOption, setInvoiceOption] = useState(deal?.invoiceOption ?? '')
  const [agreementOption, setAgreementOption] = useState(deal?.agreementOption ?? '')
  const [pipelineStage, setPipelineStage] = useState(deal?.stage ?? '')
  const [probability, setProbability] = useState(deal?.probability ?? 0)
  const [strategyLink, setStrategyLink] = useState(deal?.strategyLink ?? '')
  const [isContactCopied, setIsContactCopied] = useState(false)
  const [isAuthorizedCopied, setIsAuthorizedCopied] = useState(false)

  // Version history — replaces old predecessor/successor lineage
  const versionHistory = useMemo(() => {
    if (!PK || !deal?.deal_uuid) return null
    const orgDeals = deals?.filter(d => d?.PK === PK) ?? []
    const orgProducts = allProducts?.filter(p => p?.PK === PK) ?? []
    const { agreements } = buildMasterAgreements(orgDeals, orgProducts)

    // Find by masterAgreementId if set, fall back to UUID-based search
    const match =
      (deal.masterAgreementId
        ? agreements.find(a => a.masterAgreementId === deal.masterAgreementId)
        : null) ?? agreements.find(a => a.versions.some(v => v.deal?.deal_uuid === deal.deal_uuid))

    if (!match || match.versions.length <= 1) return null
    return match.versions
  }, [deal?.masterAgreementId, deal?.deal_uuid, PK, deals, allProducts])

  // Fallback: old predecessor/successor lineage for non-versioned deals
  const predecessorDeal = !versionHistory ? deals?.find(d => d?.SK === `DEAL_${deal?.predecessorId}`) ?? null : null
  const successorDeal = !versionHistory ? deals?.find(d => d?.SK === `DEAL_${deal?.successorId}`) ?? null : null
  const clonedFromDeal = !versionHistory ? deals?.find(d => d?.SK === `DEAL_${deal?.clonedFromId}`) ?? null : null
  const hasOldLineage = !!(predecessorDeal || successorDeal || (clonedFromDeal && !predecessorDeal))
  const hasLineage = !!versionHistory || hasOldLineage

  const ownerId = Array.isArray(deal?.owner) ? deal.owner[0] ?? null : deal?.owner ?? null
  const owner = useMemo(() => {
    if (!isUsersLoading && ownerId) return users?.find(u => u?.user_uuid === ownerId)
  }, [deal, users, isUsersLoading])
  const onChange = (fieldName, value) => {
    console.log('onChange triggered')
    // const newValue = fieldName !== 'owner' ? value : [value]
    console.log('fieldName', fieldName, 'value', value)
    if (deal) {
      syncDealFields(queryClient, deal, [{ fieldName: fieldName, newValue: value }])
    }
  }
  // useEffect(() => {
  //   debugLog('[DealInfoCard]', 'deal', deal)
  // }, [deal])
  const handleNavigate = id => {
    navigate(`/deals/${PK}/DEAL_${id}/details`)
  }
  const handleUpdateModal = (churnNote, lastDate, reasonChurn, newStatus) => {
    if (!reasonChurn || !lastDate || !churnNote) {
      showSnackbar('All fields are required.', 'error')
      return
    }
    const fieldsToUpdate = [
      { fieldName: 'lastDateOfService', newValue: lastDate },
      { fieldName: 'reasonChurn', newValue: reasonChurn },
      { fieldName: 'churnNote', newValue: churnNote },
      { fieldName: 'dealStatus', newValue: newStatus },
    ]
    handleDealStatusModalUpdate({
      churnNote,
      lastDate,
      reasonChurn,
      newStatus,
      onSuccess: () => {
        setShowModal(false)
        if (deal) {
          syncDealFields(queryClient, deal, fieldsToUpdate)
        }
      },
    })
  }
  const handleUpdate = async (fieldName, newValue) => {
    const timelineData = updateDealTimelineData(
      deal?.deal_uuid,
      activeUser,
      'Update',
      'deals',
      `Updated`,
      `${fieldName} update to ${newValue}`
    )
    if (deal[fieldName] !== newValue) {
      updateItem(
        {
          PK: deal?.PK,
          SK: deal?.SK,
          tableName: 'ORGANIZATIONS_TABLE',
          fieldName,
          // newValue: fieldName !== 'owner' ? newValue : [newValue],
          newValue: newValue,
        },
        {
          onSuccess: () => {
            const updatedData = { [fieldName]: newValue }

            if (deal) {
              syncDealFields(queryClient, deal, [{ fieldName: fieldName, newValue: newValue }])
            }
            showSnackbar(`Deal ${fieldName} updated successfully`, 'success')
            createTimelineEntry(timelineData)
          },
          onError: err => {
            console.error('Update failed', err)
          },
        }
      )
    }
  }

  const handleCancelModal = () => {
    setShowModal(false)
    setTempDealStatus('')
    if (deal) {
      syncDealFields(queryClient, deal, [{ fieldName: 'dealStatus', newValue: deal?.dealStatus }])
    }
  }
  const handleInvoiceOptionChange = async event => {
    const selectedInvoiceOption = event.target.value
    setInvoiceOption(selectedInvoiceOption)

    await handleUpdate('invoiceOption', selectedInvoiceOption)

    if (deal) {
      syncDealFields(queryClient, deal, [{ fieldName: 'invoiceOption', newValue: selectedInvoiceOption }])
    }
  }

  const handleAgreementOptionChange = async event => {
    const selectedAgreementOption = event.target.value
    setAgreementOption(selectedAgreementOption)

    await handleUpdate('agreementOption', selectedAgreementOption)

    if (deal) {
      syncDealFields(queryClient, deal, [{ fieldName: 'agreementOption', newValue: selectedAgreementOption }])
    }
  }
  const handlePipelineStageChange = async newValue => {
    setPipelineStage(newValue)
    await handleUpdate('stage', newValue)

    if (deal) {
      syncDealFields(queryClient, deal, [{ fieldName: 'stage', newValue: newValue }])
    }
  }
  const handleFieldChange = (fieldName, newValue) => {
    // console.log('[handleFieldChange] fieldName', fieldName, 'newValue', newValue)
    if (fieldName === 'probability') {
      setProbability(newValue)
    } else {
      setStrategyLink(newValue)
    }
  }

  const handleDealStatusChange = async (fieldName, newValue) => {
    // console.log('[handleFieldChange] fieldName', fieldName, 'newValue', newValue)
    await handleUpdate('dealStatus', newValue)
    if (deal) {
      syncDealFields(queryClient, deal, [{ fieldName: 'dealStatus', newValue: newValue }])
    }
  }

  const handleBlur = event => {
    const { name, value } = event.target
    if (deal && deal[name] !== value) {
      console.log('(NEW VALUE DETECTED)updating after blur')
      syncDealFields(queryClient, deal, [{ fieldName: name, newValue: value }])
      handleUpdate(name, value)
    }
  }
  return (
    <Grid
      container
      sx={{
        bgcolor: theme.palette.background.section,
        borderRadius: '12px',
        overflow: 'hidden',
        alignContent: 'start',
      }}
    >
      {/* ─── Left column: Deal Info ─── */}
      <Grid item xs={7} sx={{ p: 2.5, pt: 6.5, borderRight: '1px solid', borderColor: 'divider' }}>
        <Grid container spacing={4.5}>
          <Grid item xs={6}>
            <LabelValue label="Owner" value={owner?.name || 'No Owner'} />
          </Grid>
          <Grid item xs={6}>
            <LabelValue label="Contacts">
              {newContacts?.length > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden', minWidth: 0 }}>
                  <StyledLink
                    to={`/contacts/${newContacts[0]?.contacts_uuid}/details`}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {newContacts[0]?.name}
                  </StyledLink>
                  {newContacts.length > 1 && (
                    <Tooltip
                      title={
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {newContacts.slice(1).map(c => (
                            <StyledLink
                              key={c?.contacts_uuid}
                              to={`/contacts/${c?.contacts_uuid}/details`}
                              style={{ color: 'inherit', fontSize: 13 }}
                            >
                              {c?.name}
                            </StyledLink>
                          ))}
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <Typography
                        component="span"
                        sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      >
                        +{newContacts.length - 1}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              ) : (
                <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>—</Typography>
              )}
            </LabelValue>
          </Grid>

          <Grid item xs={6}>
            <LabelValue label="Organization">
              {organization ? (
                <StyledLink
                  to={`/organizations/ORG_${orgId}/details`}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  <RenderDealInfoLink organization={organization} />
                </StyledLink>
              ) : (
                <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>—</Typography>
              )}
            </LabelValue>
          </Grid>
          <Grid item xs={6}>
            <LabelValue label="Contracted By">
              {contractedBy ? (
                <StyledLink
                  to={`/organizations/ORG_${contractedBy?.org_uuid}/details`}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  <RenderDealInfoLink organization={contractedBy} />
                </StyledLink>
              ) : (
                <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>—</Typography>
              )}
            </LabelValue>
          </Grid>

          <Grid item xs={6}>
            <LabelValue
              label="Auth Recipient"
              items={deal?.recipients?.authorizedRecipient}
              displayKey="email"
              route={item => `/users/${item.id}`}
            />
          </Grid>
          <Grid item xs={6}>
            <LabelValue
              label="Contact Recipient"
              items={deal?.recipients?.contactRecipient}
              displayKey="email"
              route={item => `/contacts/${item.id}`}
            />
          </Grid>

          <Grid item xs={6}>
            <LabelValue
              label="Auth Link"
              value={deal?.authorizedDocumentLink && deal?.linksGenerated ? deal.authorizedDocumentLink : null}
              to={deal?.authorizedDocumentLink}
              external
              copyable
              isCopied={isAuthorizedCopied}
              onCopy={() => handleCopy(deal?.authorizedDocumentLink, setIsAuthorizedCopied)}
              displayText="View"
            />
          </Grid>
          <Grid item xs={6}>
            <LabelValue
              label="Contact Link"
              value={deal?.contactDocumentLink && deal?.linksGenerated ? deal.contactDocumentLink : null}
              to={deal?.contactDocumentLink}
              external
              copyable
              isCopied={isContactCopied}
              onCopy={() => handleCopy(deal?.contactDocumentLink, setIsContactCopied)}
              displayText="View"
            />
          </Grid>

          {/* Deal Lineage — full width */}
          <Grid item xs={12}>
            <InfoLabel sx={{ mb: 0.75 }}>Deal Lineage</InfoLabel>
            {versionHistory ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {versionHistory.map((v, i) => {
                  const isCurrent = v.deal?.deal_uuid === deal?.deal_uuid
                  return (
                    <Box key={v.deal?.deal_uuid ?? i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {i > 0 && (
                        <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.5, flexShrink: 0 }} />
                      )}
                      <DealChainNode
                        deal={v.deal}
                        label={isCurrent ? 'Current' : v.isRoot ? 'Original' : 'Revision'}
                        version={v.semanticVersion}
                        isCurrent={isCurrent}
                        onClick={!isCurrent ? () => handleNavigate(v.deal?.deal_uuid) : undefined}
                      />
                    </Box>
                  )
                })}
              </Box>
            ) : hasOldLineage ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                {(predecessorDeal || (clonedFromDeal && !predecessorDeal)) && (
                  <>
                    <DealChainNode
                      deal={predecessorDeal || clonedFromDeal}
                      label={predecessorDeal ? 'Predecessor' : 'Cloned From'}
                      onClick={() => handleNavigate((predecessorDeal || clonedFromDeal)?.deal_uuid)}
                    />
                    <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.5, flexShrink: 0 }} />
                  </>
                )}
                <DealChainNode deal={deal} label="Current" isCurrent />
                {successorDeal && (
                  <>
                    <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.5, flexShrink: 0 }} />
                    <DealChainNode
                      deal={successorDeal}
                      label="Successor"
                      onClick={() => handleNavigate(successorDeal?.deal_uuid)}
                    />
                  </>
                )}
              </Box>
            ) : (
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>No related deals</Typography>
            )}
          </Grid>
        </Grid>
      </Grid>

      {/* ─── Right column: Editable fields ─── */}
      <Grid item xs={5} sx={{ p: 2.5, pt: 6.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <SingleUserSelect
          name="owner"
          value={owner ?? null}
          options={users ?? []}
          displayLabel="Owner"
          onChange={selected => {
            onChange('owner', selected)
            if (selected?.user_uuid === deal?.owner?.[0]) return
            if (selected && selected?.user_uuid && selected?.user_uuid !== deal?.owner?.[0])
              handleUpdate('owner', [selected?.user_uuid])
          }}
        />
        <DealStatusSelect
          newDeal={deal}
          onSaveModal={handleUpdateModal}
          onChange={handleDealStatusChange}
          setShowModal={setShowModal}
          setTempDealStatus={setTempDealStatus}
          label={true}
          setIsModalSave={setIsModalSave}
        />
        <SelectInvoiceOption
          invoiceOption={invoiceOption}
          onChange={handleInvoiceOptionChange}
          validationErrors={validationErrors}
          isSelected={true}
        />
        <SelectAgreementOption
          agreementOption={agreementOption}
          onChange={handleAgreementOptionChange}
          validationErrors={validationErrors}
          isSelected={true}
        />
        <CustomOptionSelect
          stateKey={'stage'}
          value={deal?.stage}
          options={dealStageOptions}
          displayLabel={'Pipeline Stage'}
          setterFn={handlePipelineStageChange}
          minWidth={'100%'}
        />
        <TextField
          label="Probability"
          name="probability"
          value={probability ?? 0}
          onChange={e => handleFieldChange('probability', e.target.value)}
          onBlur={event => handleBlur(event)}
          fullWidth
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          inputProps={{ min: 0, max: 100 }}
          sx={{ m: '0px !important' }}
        />
        <TextField
          label="Strategy Link"
          placeholder="Link to Strategy"
          name="strategyLink"
          value={strategyLink ?? ''}
          onChange={e => handleFieldChange('strategyLink', e.target.value)}
          onBlur={event => handleBlur(event)}
          fullWidth
          type="text"
          sx={{ m: '0px !important' }}
        />
      </Grid>
      {/* Points billing summary */}
      {deal?.billingModel === 'points' && (() => {
        // Compute total points across all products accounting for billing months
        const totalPoints = productRows?.reduce((sum, p) => {
          const pts = Number(p.points) || 0
          if (!pts) return sum
          const dates = p.dateRange || [p.startDate, p.endDate]
          if (dates?.[0] && dates?.[1]) {
            const s = new Date(dates[0])
            const e = new Date(dates[1])
            const months = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1
            return sum + pts * Math.max(months, 1)
          }
          return sum + pts
        }, 0) ?? 0
        const rate = deal?.ratePerPoint ?? 0
        return (
        <Grid
          item
          xs={12}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2.5,
            pt: 2,
            pb: 0.5,
          }}
        >
          {[
            { label: 'Total Points', value: totalPoints, format: 'number' },
            { label: 'Rate / Point', value: rate, format: 'currency' },
            { label: 'Points Total', value: totalPoints * rate, format: 'currency' },
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                textAlign: 'center',
                py: 0.75,
                px: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.info.main, 0.06),
                border: '1px solid',
                borderColor: alpha(theme.palette.info.main, 0.15),
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'text.secondary',
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'info.main',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {item.format === 'currency' ? `$${formatNumber(item.value)}` : item.value}
              </Typography>
            </Box>
          ))}
        </Grid>
        )
      })()}
      <Grid
        item
        xs={12}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          p: 2.5,
        }}
      >
        {[
          { label: 'Gross Profit', value: displayTotals?.grossProfit },
          {
            label:
              deal && ['CSA', 'RCSA'].includes(deal.type) && deal.numberOfPayments
                ? 'Per-Payment (CSA)'
                : 'Monthly Total',
            value:
              deal && ['CSA', 'RCSA'].includes(deal.type) && deal.numberOfPayments
                ? displayTotals?.paymentAmount
                : displayTotals?.monthlyTotal,
          },
          {
            label: 'Total Gross Profit',
            value:
              deal && ['CSA', 'RCSA'].includes(deal.type) && deal.numberOfPayments
                ? displayTotals?.grossProfit
                : displayTotals?.totalGrossProfit,
          },
          {
            label: 'Total',
            value:
              deal && ['CSA', 'RCSA'].includes(deal.type) && deal.numberOfPayments
                ? displayTotals?.monthlyTotal
                : displayTotals?.total,
            highlight: true,
          },
        ].map((item, index) => (
          <Box
            key={index}
            sx={{
              textAlign: 'center',
              py: 1,
              px: 1,
              borderRadius: 1.5,
              bgcolor: item.highlight ? alpha(theme.palette.text.highlight, 0.08) : alpha(theme.palette.text.primary, 0.03),
              border: '1px solid',
              borderColor: item.highlight ? alpha(theme.palette.text.highlight, 0.2) : 'divider',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: 9,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'text.secondary',
              }}
            >
              {item.label}
            </Typography>
            {displayTotals && (
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: item.highlight ? 'primary.main' : theme.palette.text.complete,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {`$${formatNumber(item.value)}`}
              </Typography>
            )}
          </Box>
        ))}
      </Grid>
      {showModal && (
        <DealStatusModal
          isOpen={showModal}
          onClose={handleCancelModal}
          onSave={handleUpdateModal}
          tempDealStatus={tempDealStatus}
        />
      )}
    </Grid>
  )
}

export default DealInfoCard
